'use client';

import Cookies from 'js-cookie';
import { type PropsWithChildren, createContext, use } from 'react';
import { create, createStore } from 'zustand';
import type { StateCreator, StoreApi } from 'zustand';

import { useAuthStore } from '@sola/auth-sdk/store.tsx';
import type {
  CartItemOptions,
  CartItemPayload,
  CartUpdateItemReqBody,
} from '@sola/schema/dotcom/cart/api.ts';
import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { Cart } from '@sola/schema/dotcom/cart/cart.ts';
import { CartUpsellActionEnum } from '@sola/schema/dotcom/cart/cart.ts';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import type { ErrorCart } from '@sola/schema/dotcom/cart/errors.ts';
import { CartErrorTypeEnum } from '@sola/schema/dotcom/cart/errors.ts';
import type {
  CartItemPaymentPayload,
  PaymentPayload,
} from '@sola/schema/dotcom/cart/payment.ts';
import { SponsorshipItemTypeEnum } from '@sola/schema/dotcom/cart/sponsorship.ts';
import type { ErrorBase } from '@sola/schema/dotcom/shared/errors.ts';
import { COOKIE_NAME_CART } from '@sola/shared-utils/constants.ts';
import {
  userErrorCartGeneric,
  userErrorCartItemsAdd,
} from '@sola/shared-utils/errors/cart.ts';
import { isUserError } from '@sola/shared-utils/httpClient.ts';
import {
  type ZustandSelectedSlices,
  type ZustandSliceKey,
  useZustandSlices,
} from '@sola/shared-utils/react.tsx';

import {
  addToCart,
  fetchCart,
  fetchRestoredCart,
  logOut,
  removeFromCart,
  updatePaymentItems as updateCartPaymentItems,
  updateItem,
  updateUpsell,
} from './cart.ts';
import { CartQueryParam } from './queryparams.ts';

const authStore = useAuthStore.getState;

export interface CartActions {
  addItems: (request: {
    items: CartItemPayload | CartItemPayload[];
    options?: CartItemOptions;
    supporterId?: string;
  }) => Promise<ErrorCart[] | undefined>;
  addChild: (
    childId: string,
    sourceCode?: string | null,
    consignmentHoldId?: string | null,
  ) => Promise<ErrorCart[] | undefined>;
  clearSession: () => void;
  getCart: (forceRefresh?: boolean) => Promise<void>;
  restoreCart: (cartId: string) => Promise<void>;
  removeItem: (id: CartItem['id']) => Promise<ErrorCart[] | undefined>;
  toggleItemBusy: (id: CartItem['id']) => void;
  setPerformedInitialFetch: () => void;
  updateCartState: (args: { cart: Cart | null; errors?: ErrorCart[] }) => void;
  updateCommitmentItems: (
    commitmentItems: {
      commitmentId: PaymentPayload['commitmentId'];
      unitPrice: CartItemPaymentPayload['unitPrice'];
    }[],
  ) => Promise<ErrorCart[] | undefined>;
  updateItem: (
    id: CartItem['id'],
    updates: CartUpdateItemReqBody,
  ) => Promise<ErrorCart[] | undefined>;
  acceptUpsellItem: (id?: string) => Promise<void>;
  declineUpsellItem: (id?: string) => Promise<void>;
  removeAuthOnlyItems: () => Promise<void>;
  clearErrors: () => void;
  clearUpsellErrors: () => void;
  removeError: (error: ErrorBase) => void;
  refreshSupporterCart: () => Promise<void>;
  goToCheckout: () => void;
  clearCheckoutRedirect: () => void;
}

export interface CartState {
  cart: Cart | null;
  errors: ErrorCart[] | [];
  isAddingItems: boolean;
  isRedirectingToCheckout: boolean;
  isReady: boolean;
  itemsBusy: CartItem['id'][] | [];
  referrer?: string;
  hasRecurring: () => boolean;
  actions: CartActions;
}

export type CartStoreApi = StoreApi<CartState>;
export const CHECKOUT_REDIRECT_TIMEOUT_MS = 10_000;

// TODO: Similar to checkout sdk, we probably want to centralize our async request and error logic.
const createCartState: StateCreator<CartState> = (set, get) => {
  let checkoutRedirectTimeout: ReturnType<typeof setTimeout> | undefined;

  const clearCheckoutRedirectTimeout = () => {
    clearTimeout(checkoutRedirectTimeout);
    checkoutRedirectTimeout = undefined;
  };

  return {
    cart: null,
    errors: [],
    isAddingItems: false,
    isRedirectingToCheckout: false,
    itemsBusy: [],
    isReady: false,
    referrer: undefined,
    hasRecurring: () => {
      const { cart } = get();

      return (
        !!cart?.items?.length &&
        cart.items.some(
          (item) =>
            item.commitmentFrequency !== CommitmentFrequencyEnum.OneTime,
        )
      );
    },
    actions: {
      /**
       * Add items to cart
       */
      addItems: async ({ items, options, supporterId }) => {
        const {
          itemsBusy,
          actions: { toggleItemBusy, updateCartState },
        } = get();

        if (itemsBusy?.length) {
          return;
        }

        // We just need some sort of placeholder to add to the busy items array.
        const addItemsId = 'addingItems';
        const { referrer } = get();
        try {
          toggleItemBusy(addItemsId);
          const fetchedCart = await addToCart({
            items,
            options,
            supporterId,
            referrer,
          });
          if (!fetchedCart) {
            throw new Error('unable to retrieve cart');
          }
          const { errors } = fetchedCart;

          updateCartState({
            cart: fetchedCart,
            errors,
          });

          return errors;
        } catch (error) {
          const { errors } = get();
          const newErrors = [
            ...errors,
            ...(isUserError(error) ? [error] : [userErrorCartItemsAdd()]),
          ];
          set({
            errors: newErrors,
          });

          return newErrors;
        } finally {
          toggleItemBusy(addItemsId);
        }
      },

      /** Add a child to the cart */
      addChild: async (childId, sourceCode, consignmentHoldId) => {
        const {
          actions: { addItems },
        } = get();

        if (consignmentHoldId) {
          return await addItems({
            items: {
              itemType: CartItemTypeEnum.Sponsorship,
              sponsorship: {
                sponsorshipType: SponsorshipItemTypeEnum.FirstPayment,
                globalId: childId,
                sourceCode: sourceCode ?? '',
                holdId: consignmentHoldId,
                isConsignment: true,
              },
            },
          });
        }

        return await addItems({
          items: {
            itemType: CartItemTypeEnum.Sponsorship,
            sponsorship: {
              sponsorshipType: SponsorshipItemTypeEnum.Selected,
              globalId: childId,
              ...(sourceCode && { sourceCode }),
            },
          },
        });
      },

      /**
       * Clear cart session.
       * //TODO: cart cookie isn't accessible from JavaScript. Do we even need this?
       */
      clearSession: () => {
        const { updateCartState } = get().actions;
        Cookies.remove(COOKIE_NAME_CART);
        updateCartState({ cart: null });
      },

      /**
       * Get either an existing or new cart.
       */
      getCart: async (forceRefresh = false) => {
        set({ isReady: false });

        const {
          cart,
          actions: { updateCartState },
        } = get();

        if (cart && !forceRefresh) {
          set({ isReady: true });

          // NOTE: should this be returning the cart here?
          return; // cart // (?);
        }
        const { referrer } = get();
        try {
          const fetchedCart = await fetchCart({
            ...(referrer && { referrer }),
          });
          if (!fetchedCart) {
            throw new Error('unable to retrieve cart');
          }

          updateCartState({
            cart: fetchedCart,
            errors: fetchedCart.errors,
          });
        } catch (error) {
          const { errors } = get();
          set({
            errors: [
              ...errors,
              isUserError(error) ? error : userErrorCartGeneric(),
            ],
          });
        }

        set({ isReady: true });
      },

      /**
       * Restore an existing cart by id.
       */
      restoreCart: async (cartId: string) => {
        const { updateCartState } = get().actions;
        set({ isReady: false });
        try {
          // TODO: Would we want/need supporterId?
          const fetchedCart = await fetchRestoredCart(cartId);
          if (!fetchedCart) {
            throw new Error('There was an issue restoring the cart.');
          }

          const { errors } = fetchedCart;
          updateCartState({
            cart: fetchedCart,
            errors,
          });
        } catch (error) {
          const { errors } = get();
          set({
            errors: [
              ...errors,
              isUserError(error) ? error : userErrorCartGeneric(),
            ],
          });
        }

        set({ isReady: true });
      },

      /**
       * Refresh cart when supporter logs in/out
       */
      refreshSupporterCart: async () => {
        const { actions } = get();
        set({ isReady: false });
        try {
          await (authStore().isLoggedIn()
            ? actions.getCart(true)
            : actions.removeAuthOnlyItems());
        } catch (error) {
          const { errors } = get();
          set({
            errors: [
              ...errors,
              isUserError(error) ? error : userErrorCartGeneric(),
            ],
          });
        }

        set({ isReady: true });
      },

      /**
       * Remove an existing item from the cart.
       */
      removeItem: async (id) => {
        if (!id) {
          return;
        }
        const {
          itemsBusy,
          actions: { toggleItemBusy, updateCartState },
        } = get();

        if (itemsBusy?.length) {
          return;
        }
        toggleItemBusy(id);

        try {
          const fetchedCart = (await removeFromCart(id)) || null;
          if (!fetchedCart) {
            throw new Error('There was an issue removing items...');
          }
          const { errors } = fetchedCart;

          updateCartState({ cart: fetchedCart, errors });
        } catch (error) {
          const { errors } = get();
          const newErrors = [
            ...errors,
            isUserError(error) ? error : userErrorCartGeneric(),
          ];
          set({
            errors: newErrors,
          });

          return newErrors;
        } finally {
          toggleItemBusy(id);
        }
      },

      /**
       * Indicate when cart has initially loaded.
       * //TODO: Do we want to rename this to setIsReady?
       */
      setPerformedInitialFetch: () => set({ isReady: true }),

      /**
       * Add or remove item from busy queue.
       */
      toggleItemBusy: (id) => {
        const updatedBusyItems = [...get().itemsBusy];
        if (updatedBusyItems.includes(id)) {
          updatedBusyItems.splice(updatedBusyItems.indexOf(id), 1);
        } else {
          updatedBusyItems.push(id);
        }

        set({
          itemsBusy: updatedBusyItems,
        });
      },

      /**
       * Update the cart state.
       * This function will allow us to centralize logic we need on every update.
       */
      updateCartState: ({ cart, errors = [] }) => {
        set({
          cart,
          errors: [...errors],
        });
      },

      /**
       * Update commitment items in a single request.
       */
      updateCommitmentItems: async (
        commitmentItems,
      ): Promise<ErrorCart[] | undefined> => {
        const {
          cart,
          itemsBusy,
          actions: { toggleItemBusy, updateCartState },
        } = get();

        const paymentItems: CartItemPaymentPayload[] = commitmentItems.map(
          ({ commitmentId, unitPrice }) => ({
            itemType: CartItemTypeEnum.Payment as const,
            payment: {
              commitmentId,
            },
            unitPrice,
          }),
        );

        if (itemsBusy?.length) {
          return;
        }

        const updateCommitmentItemsId = 'updatingCommitmentItems';

        try {
          toggleItemBusy(updateCommitmentItemsId);
          const fetchedCart =
            (await updateCartPaymentItems({
              cartId: cart?.id,
              paymentItems,
            })) || null;

          if (!fetchedCart) {
            throw new Error('unable to retrieve cart');
          }

          const { errors } = fetchedCart;
          updateCartState({
            cart: fetchedCart,
            errors,
          });

          return errors;
        } catch (error) {
          const { errors } = get();
          const newErrors = [
            ...errors,
            isUserError(error) ? error : userErrorCartGeneric(),
          ];
          set({
            errors: newErrors,
          });

          return newErrors;
        } finally {
          toggleItemBusy(updateCommitmentItemsId);
        }
      },

      /**
       * Update an existing cart item.
       */
      updateItem: async (id, updates): Promise<ErrorCart[] | undefined> => {
        const {
          itemsBusy,
          actions: { toggleItemBusy, updateCartState },
        } = get();

        if (itemsBusy?.length) {
          return;
        }
        try {
          toggleItemBusy(id);
          const fetchedCart = (await updateItem(id, updates)) || null;

          if (!fetchedCart) {
            throw new Error('unable to retrieve cart');
          }
          const { errors } = fetchedCart;
          updateCartState({
            cart: fetchedCart,
            errors,
          });

          return errors;
        } catch (error) {
          // TODO Currently this is returning errors to consumers. Might want to think about refactoring.
          console.error('Unexpected error type caught in useCart:', error);
          return [isUserError(error) ? error : userErrorCartGeneric()];
        } finally {
          toggleItemBusy(id);
        }
      },

      /**
       * Update upsell item, increment accept count
       */
      acceptUpsellItem: async (id): Promise<void> => {
        const {
          actions: { updateCartState },
        } = get();
        try {
          const fetchedCart = await updateUpsell({
            action: CartUpsellActionEnum.IncrementAcceptCount,
            itemId: id,
          });

          if (!fetchedCart) {
            throw new Error('unable to accept upsell');
          }

          const { errors } = fetchedCart;
          updateCartState({
            cart: fetchedCart,
            // The error returned could be related to a lost hold or something - likely should present it...
            errors,
          });
        } catch (error) {
          // swallow error and don't show user
          console.warn('acceptUpsellItem error:', error);
        }
      },

      /**
       * Update upsell item, increment decline count
       */
      declineUpsellItem: async (id): Promise<void> => {
        const {
          actions: { updateCartState },
        } = get();
        try {
          const fetchedCart = await updateUpsell({
            action: CartUpsellActionEnum.IncrementDeclineCount,
            itemId: id,
          });

          if (!fetchedCart) {
            throw new Error('unable to decline upsell');
          }

          const { errors } = fetchedCart;
          updateCartState({
            cart: fetchedCart,
            // The error returned could be related to a lost hold or something - likely should present it...
            errors,
          });
        } catch (error) {
          // swallow error and don't show user
          console.warn('declineUpsellItem error:', error);
        }
      },

      /**
       * Remove items that are only available to authenticated supporters.
       */
      removeAuthOnlyItems: async () => {
        const { updateCartState } = get().actions;
        set({ isReady: false });

        try {
          const fetchedCart = await logOut();
          if (fetchedCart) {
            const { errors } = fetchedCart;
            updateCartState({ cart: fetchedCart, errors });
          }
        } catch (error) {
          const { errors } = get();
          set({
            errors: [
              ...errors,
              isUserError(error) ? error : userErrorCartGeneric(),
            ],
          });
        }

        set({ isReady: true });
      },
      clearErrors: () => {
        set({
          errors: [],
        });
      },
      clearUpsellErrors: () => {
        const { errors } = get();
        const newErrors = errors.reduce((acc: ErrorCart[], cur) => {
          if (cur.type !== CartErrorTypeEnum.HoldSponsoredUpsell) {
            return [...acc, cur];
          }

          return acc;
        }, []);

        set({
          errors: newErrors,
        });
      },
      removeError: (error: ErrorCart | ErrorBase) => {
        const { errors } = get();
        const clonedErrors = structuredClone(errors);
        const filteredErrors = clonedErrors.filter(
          (err) => err.id !== error.id,
        );

        set({
          errors: filteredErrors,
        });
      },
      goToCheckout: () => {
        if (globalThis) {
          const { cart } = get();

          set({ isRedirectingToCheckout: true });

          clearCheckoutRedirectTimeout();
          checkoutRedirectTimeout = setTimeout(() => {
            get().actions.clearCheckoutRedirect();
          }, CHECKOUT_REDIRECT_TIMEOUT_MS);

          globalThis.location.href =
            cart?.checkoutUrl ?? process.env.BASE_URL_CHECKOUT ?? '';
        }
      },
      clearCheckoutRedirect: () => {
        /**
         * Fallback for resetting `isRedirectingToCheckout` when the checkout navigation
         * starts but never unloads this page (user presses Stop/Esc, a beforeunload
         * prompt is cancelled, hash-only URL, 204 or download response).
         */
        clearCheckoutRedirectTimeout();
        set({ isRedirectingToCheckout: false });
      },
    },
  };
};

export const createCartStore = () => createStore<CartState>()(createCartState);

export const useCartStore = create<CartState>()(createCartState);

const CartStoreContext = createContext<CartStoreApi | null>(null);

export const CartStoreProvider = ({
  children,
  store,
}: PropsWithChildren<{ store: CartStoreApi }>) => {
  return <CartStoreContext value={store}>{children}</CartStoreContext>;
};

/*
 * Hook that allows consumers to easily implement slices on cart store.
 */
export const useCart = <
  const TKeys extends readonly ZustandSliceKey<CartState>[],
>(
  ...props: TKeys
): ZustandSelectedSlices<CartState, TKeys> => {
  const store = use(CartStoreContext) ?? useCartStore;

  return useZustandSlices<CartState, TKeys>(store, ...props);
};

/**
 * Function to initialize cart store.
 */
export const initCart = async () => {
  const { searchParams } = new URL(globalThis.location.href);
  const referrer = (
    searchParams?.get(CartQueryParam.Referrer) ||
    searchParams?.get(CartQueryParam.Referer)
  )
    ?.toString()
    .trim();

  if (referrer) {
    useCartStore.setState({ referrer });
  }
};
